import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useBlocker, useLocation, useNavigate } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { SharedRoastery } from "./SharedRoastery";
import { LiveRecorder } from "./LiveRecorder";
import { CoffeeLibrary } from "./Discovery";
import { ProductionPlan } from "./ProductionPlan";
import { RoastWorkspace } from "./RoastWorkspace";
import { YieldCost } from "./YieldCost";
import { type Workspace } from "./workspace";
import { useLocalWorkspace } from "./useLocalWorkspace";
import { SourcePicker } from "./SourcePicker";
import { RoastLibrary, SaveToLibrary } from "./RoastLibrary";
import { type LibraryKind } from "./library";
import { Button } from "@/components/ui/button";
import { Notice } from "./components/workbench";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import "./roasting.css";

// Gate the whole editor so stored DialKit values cannot affect production.
const DesignTuner =
  process.env.NODE_ENV !== "production" ? lazy(() => import("./DesignTuner")) : null;
const pages = new Set([
  "",
  "history",
  "operations",
  "planning",
  "yield",
  "coffees",
  "devices",
]);
const focusWorkspace = () => document.getElementById("roasting-workbench")?.focus();

export default function RoastingWorkbench() {
  const location = useLocation(),
    navigate = useNavigate();
  const page = location.pathname.slice("/roasting".length).replace(/^\/+|\/+$/g, "");
  const {
    hasUnsavedChanges,
    workspace,
    update,
    ready,
    canSave,
    loadError,
    saveError,
    saving,
    status,
    setStatus,
    save,
  } = useLocalWorkspace();
  const [blendDirty, setBlendDirty] = useState(false), [blendBusy, setBlendBusy] = useState(false);
  const blocker = useBlocker(
    ({ nextLocation }) =>
      (page === "planning" && nextLocation.pathname !== location.pathname && (blendDirty || blendBusy)) || (!(
        nextLocation.pathname === "/roasting" ||
        nextLocation.pathname.startsWith("/roasting/")
      ) && hasUnsavedChanges()),
  );
  useEffect(() => {
    if (blocker.state !== "blocked" || blendDirty || blendBusy) return;
    const staysInRoasting = blocker.location.pathname === "/roasting" || blocker.location.pathname.startsWith("/roasting/");
    if (staysInRoasting || !hasUnsavedChanges()) blocker.proceed();
  }, [blocker, saving, status, hasUnsavedChanges, blendDirty, blendBusy]);
  useEffect(() => {
    if (!blendDirty && !blendBusy) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", prevent);
    return () => window.removeEventListener("beforeunload", prevent);
  }, [blendDirty, blendBusy]);
  const [selecting, setSelecting] = useState(false);
  const [replacement, setReplacement] = useState<Workspace | null>(null);
  const [libraryDraft, setLibraryDraft] = useState<{
    kind: LibraryKind;
    workspace: Workspace;
  } | null>(null);
  const libraryTrigger = useRef<HTMLElement | null>(null),
    replacementTrigger = useRef<HTMLElement | null>(null);
  const [tunerOpen, setTunerOpen] = useState(false),
    [tunerLoaded, setTunerLoaded] = useState(false);
  const tunerTrigger = useRef<HTMLButtonElement>(null);
  function openWorkspace(next: Workspace, trigger?: HTMLElement) {
    if (workspace) {
      replacementTrigger.current = trigger ?? (document.activeElement as HTMLElement);
      setReplacement(next);
    } else {
      update(next);
      setSelecting(false);
      navigate("/roasting");
      focusWorkspace();
      setStatus(
        canSave
          ? "Recording opened. Annotation changes save automatically in this browser."
          : "Temporary replay opened. Export to preserve your work; the existing saved data stays intact.",
      );
    }
  }
  return (
    <section
      id="roasting-workbench"
      tabIndex={-1}
      aria-label="Roasting tools"
      data-labs-shell
      className="roasting-workbench min-w-0 outline-none"
    >
      {DesignTuner && (
        <div className="mb-3 flex justify-end">
          <Button
            ref={tunerTrigger}
            variant="ghost"
            size="sm"
            aria-label="Tune UI with DialKit"
            aria-controls={tunerOpen ? "design-controls" : undefined}
            aria-expanded={tunerOpen}
            onClick={() => {
              setTunerLoaded(true);
              setTunerOpen((value) => !value);
            }}
          >
            <SlidersHorizontal />
            Tune UI
          </Button>
        </div>
      )}
      <div className="lab-page space-y-6">
        {loadError && (
          <Notice title="Local restore needs attention" error>
            <p>{loadError}</p>
            <p>
              Existing data is preserved. You can use the calculators, or open a temporary
              replay and export it.
            </p>
          </Notice>
        )}
        {saveError && (
          <Notice title="Local changes need attention" error>
            {saveError}
          </Notice>
        )}
        {!ready && (page === "" || page === "history") && (
          <p role="status" className="text-sm text-muted-foreground">
            Opening local storage…
          </p>
        )}
        {ready &&
          page === "" &&
          (workspace && !selecting ? (
            <RoastWorkspace
              workspace={workspace}
              update={update}
              onSave={() => void save()}
              saving={saving}
              canSave={canSave}
              announce={setStatus}
              onNew={() => setSelecting(true)}
              onLibrary={(kind, trigger) => {
                libraryTrigger.current = trigger;
                setLibraryDraft({ kind, workspace: structuredClone(workspace) });
              }}
            />
          ) : (
            <SourcePicker
              temporary={!canSave}
              onChoose={openWorkspace}
              onBack={workspace ? () => setSelecting(false) : undefined}
            />
          ))}
        {ready && page === "history" && (
          <RoastLibrary
            hasWorkspace={Boolean(workspace)}
            onOpen={openWorkspace}
            onReference={(reference) => {
              if (!workspace) return;
              update((current) => ({ ...current, reference }));
              setSelecting(false);
              navigate("/roasting");
              focusWorkspace();
              setStatus(
                "Loaded " +
                  reference.name +
                  (reference.profile ? ", version " + reference.profile.revision : "") +
                  " as the reference.",
              );
            }}
          />
        )}
        {page === "planning" && <ProductionPlan onDirtyChange={setBlendDirty} onBusyChange={setBlendBusy} />}
        {page === "yield" && <YieldCost />}
        {page === "coffees" && <CoffeeLibrary />}
        {page === "devices" && (
          <LiveRecorder reference={workspace?.reference ?? null} onOpen={openWorkspace} />
        )}
        {page === "operations" && (
          <SharedRoastery workspace={workspace} onOpen={openWorkspace} />
        )}
        {!pages.has(page) && (
          <Notice title="Roasting page not found">
            <Button asChild>
              <Link to="/roasting">Open the roast workspace</Link>
            </Button>
          </Notice>
        )}
        {page !== "operations" && page !== "devices" && page !== "planning" && (
          <p
            className="save-status border-t pt-4 text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]"
            role="status"
          >
            {status}
          </p>
        )}
      </div>
      {DesignTuner && tunerLoaded && (
        <Suspense
          fallback={
            <p className="sr-only" role="status">
              Loading design controls…
            </p>
          }
        >
          <DesignTuner
            open={tunerOpen}
            onClose={() => {
              setTunerOpen(false);
              tunerTrigger.current?.focus();
            }}
          />
        </Suspense>
      )}
      {libraryDraft && (
        <SaveToLibrary
          {...libraryDraft}
          trigger={libraryTrigger.current}
          onClose={() => setLibraryDraft(null)}
          onSaved={(item) =>
            setStatus(
              "Saved " +
                item.name +
                ", version " +
                item.revision +
                ", in the local library.",
            )
          }
        />
      )}
      <AlertDialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && blocker.state === "blocked") blocker.reset();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{blendDirty || blendBusy ? "Blend changes are not saved yet" : "Local changes are not saved yet"}</AlertDialogTitle>
            <AlertDialogDescription>
              {blendBusy ? "Wait for the current blend action to finish before leaving." : blendDirty
                ? "Stay here to save your recipe, or leave and discard these edits. Your last saved version stays available."
                : saveError
                ? "Stay here to export your workspace or retry saving before leaving Roasting."
                : "Saving your latest annotations before leaving Roasting…"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11">Stay in Roasting</AlertDialogCancel>
            <AlertDialogAction
              className="min-h-11"
              disabled={blendBusy}
              onClick={() => {
                if (blocker.state === "blocked") blocker.proceed();
              }}
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={replacement !== null}
        onOpenChange={(open) => {
          if (!open) setReplacement(null);
        }}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (replacementTrigger.current?.isConnected)
              replacementTrigger.current.focus();
            else focusWorkspace();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the current replay?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the active replay and its automatically saved draft. Save the
              current roast to your library or export it first if you need another copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep current replay</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!replacement) return;
                update(replacement);
                setReplacement(null);
                setSelecting(false);
                navigate("/roasting");
                setStatus(
                  "Recording opened. Replay is paused; reference and events belong to this saved recording.",
                );
              }}
            >
              Replace replay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
